import type { TFunction } from "i18next";
import { useRef, type RefObject } from "react";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000AutoCompleteInput from "../../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import type { U3120CustomerEmployeeRateHandler } from "./U3120CustomerEmployeeRateHandlers";
import { doFindEmployees, luHandleAppiError } from "../../../../U3120Service";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../../../u2000Core/context/toast/U2000ToastContext";
import { u2000ExtractAutoCompleteResults } from "../../../../../u2000Core/utils/u2000ExtractAutoCompleteResults";
import U2000InputCurrency from "../../../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000InputCalendarCustom from "../../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";

// Component props interface
interface U3120CustomerEmployeeRateDlgProps {
  u3120CustomerEmployeeRateHandler: U3120CustomerEmployeeRateHandler;
  t: TFunction;
  NS: string;
}

// Dialog component for editing customer employee rates
function U3120CustomerEmployeeRateEditDlg({ u3120CustomerEmployeeRateHandler, t, NS }: U3120CustomerEmployeeRateDlgProps) {
  const { formStateHandlers } = u3120CustomerEmployeeRateHandler;
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // Ref for the employee input to be used as default focus
  const employeeRef = useRef<HTMLInputElement>(null);

  // Fonction de recherche d'employés via U3101
  const onSearchEmployee = async (criterias: { fieldName: string; value: any }[]) => {
    try {
      const response = await doFindEmployees(u2000AxiosAuthenticated, criterias);
      return u2000ExtractAutoCompleteResults<Record<string, any>>(response.data);
    } catch (error: any) {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      return { results: [], totalCount: 0, truncated: false };
    }
  };

  // Fonction d'affichage de l'employé
  const displayEmployee = (item: any) => {
    if (!item) return "";
    const parts = [];
    if (item.codeEmployee) parts.push(item.codeEmployee);
    if (item.firstName || item.lastName) {
      parts.push(`${item.firstName || ""} ${item.lastName || ""}`.trim());
    }
    return parts.length > 0 ? parts.join(" - ") : item.id || "";
  };

  // Fonction d'extraction des données de l'employé
  const extractEmployeeData = (result: any) => {
    if (result) {
      return [
        { fieldName: "employee.id", value: result.id },
        { fieldName: "employee.codeEmployee", value: result.codeEmployee || "" },
        { fieldName: "employee.firstName", value: result.firstName || "" },
        { fieldName: "employee.lastName", value: result.lastName || "" },
      ];
    } else {
      return [
        { fieldName: "employee.id", value: null },
        { fieldName: "employee.codeEmployee", value: null },
        { fieldName: "employee.firstName", value: null },
        { fieldName: "employee.lastName", value: null },
      ];
    }
  };

  // Unified handler that manages Save and Cancel differently
  const handleClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      // Temporarily disable onBlur validation to prevent unwanted validation on cancel
      const originalOnBlur = formStateHandlers.onBlur;
      formStateHandlers.onBlur = () => {
        /* Ignore onBlur during cancel */
      };

      // Close dialog immediately without validation
      u3120CustomerEmployeeRateHandler.onDlgClose("cancel");

      // Restore original onBlur handler after dialog closes
      setTimeout(() => {
        formStateHandlers.onBlur = originalOnBlur;
      }, 0);
    } else if (action === "save") {
      // Normal save flow with validation
      u3120CustomerEmployeeRateHandler.onDlgClose("save");
    }
  };

  // render
  return (
    <U2000SaveCancelDialog
      isOpen={u3120CustomerEmployeeRateHandler.isDialogOpen}
      onClose={handleClose}
      title={t("lblCustomerEmployeeRate", { ns: NS })}
      isValid={true}
      defaultFocusRef={employeeRef as RefObject<HTMLElement>}
      contentOverflowVisible
    >
      <div className="flex flex-col gap-y-4">
        <div>
          <U2000AutoCompleteInput
            formStateHandlers={formStateHandlers}
            fieldName="employee"
            mainFieldName="codeEmployee"
            entityLink="U3101"
            label={t("lblEmployee", { ns: NS })}
            onSearch={onSearchEmployee}
            displayFn={displayEmployee}
            overrideExtractDataFn={extractEmployeeData}
            minCarLaunchSearch={2}
            resultLabels={[]}
            resultFields={["codeEmployee", "firstName", "lastName"]}
            required={true}
            inputRef={employeeRef as React.RefObject<HTMLInputElement>}
          />
        </div>
        <div>
          <U2000InputCurrency fieldName="hourRate" label={t("lblHourRate", { ns: NS })} formStateHandlers={formStateHandlers} required={true} />
        </div>
        <U2000InputCalendarCustom formStateHandlers={formStateHandlers} fieldName="validFrom" label={t("lblValidFrom", { ns: NS })} required />
        <U2000InputCalendarCustom formStateHandlers={formStateHandlers} fieldName="validTo" label={t("lblValidTo", { ns: NS })} />
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3120CustomerEmployeeRateEditDlg;
